/**
 * Generates a clean, enterprise-friendly explainability summary.
 * Categorises triggered information units into Information, Evidence, and Rules.
 * Sorted by internal potency (never exposed to the user).
 * Pure function — no side effects, no API calls.
 */

// ---------------------------------------------------------------------------
// Node-type → category mapping
// ---------------------------------------------------------------------------

const RULES_TYPES = new Set([
  'rule_gate', 'threshold_gate', 'temporal_gate',
  'outcome_node', 'decision', 'decision_outcome',
  'action_output', 'override', 'router',
]);

const EVIDENCE_TYPES = new Set([
  'verbatim_chunk', 'metric', 'event_input',
  'condition_node', 'accumulator', 'aggregator',
]);

function categorize(nodeType) {
  const t = (nodeType || '').toLowerCase();
  if (RULES_TYPES.has(t)) return 'rules';
  if (EVIDENCE_TYPES.has(t)) return 'evidence';
  return 'information';
}

// ---------------------------------------------------------------------------
// Date formatting
// ---------------------------------------------------------------------------

function formatDate(raw) {
  if (!raw) return '';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// Text cleaning — strip synapse metadata, humanise labels
// ---------------------------------------------------------------------------

const SYNAPSE_META_RE = /Node\s+'[^']*'\s+provides\s+evidence\s+for\s+verbatim\s+chunk\s+content\s*\(w=[\d.]+\)/gi;

function cleanDescription(text) {
  if (!text) return '';
  let cleaned = text.replace(SYNAPSE_META_RE, '');
  cleaned = cleaned.replace(/[\u25CF\u25CB\u2022]/g, ' ');
  cleaned = cleaned.replace(/\s{2,}/g, ' ').replace(/\n+/g, ' ').trim();
  return cleaned;
}

function firstSentence(text, maxLen = 120) {
  if (!text) return '';
  const match = text.match(/^[^.!?\n]+[.!?]?/);
  const sentence = (match ? match[0] : text).trim();
  if (sentence.length <= maxLen) return sentence;
  return sentence.slice(0, maxLen).replace(/\s+\S*$/, '') + '...';
}

const VERBATIM_LABEL_RE = /^\[Verbatim\]\s*Chunk\s*\d+$/i;
const SYSTEM_ID_RE = /^[A-Z][A-Z0-9_]{4,}$/;
const UUID_LABEL_RE = /^(node:)?[0-9a-f]{6,}/i;

function cleanLabel(label, description, sourceDoc) {
  if (!label) return '';

  if (VERBATIM_LABEL_RE.test(label)) {
    if (sourceDoc) return sourceDoc + ' (excerpt)';
    const sentence = firstSentence(cleanDescription(description), 60);
    return sentence || label;
  }

  if (UUID_LABEL_RE.test(label)) {
    if (sourceDoc) return sourceDoc + ' (excerpt)';
    const sentence = firstSentence(cleanDescription(description), 60);
    return sentence || label;
  }

  if (SYSTEM_ID_RE.test(label)) {
    const stripped = label
      .replace(/^(RULE|NODE|SYN|ITEM)_/i, '')
      .replace(/_/g, ' ')
      .trim();
    const parts = stripped.split(/\s+/);
    const prefixParts = [];
    const meaningfulParts = [];
    let pastPrefix = false;
    for (const p of parts) {
      if (!pastPrefix && /^(ITEM\d*|\d+|[A-Z]{1,3}\d+)$/i.test(p)) {
        prefixParts.push(p);
      } else {
        pastPrefix = true;
        meaningfulParts.push(p.charAt(0).toUpperCase() + p.slice(1).toLowerCase());
      }
    }
    const meaningful = meaningfulParts.join(' ');
    const prefix = prefixParts.join(' ');
    if (meaningful && prefix) return `${meaningful} (${prefix})`;
    if (meaningful) return meaningful;
    return stripped.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  }

  return label.replace(/^\[Verbatim\]\s*/i, '').trim() || label;
}

function bestDescription(n) {
  const nlText = (n.nl_text || '').trim();
  const desc = (n.description || '').trim();
  if (nlText && nlText !== desc) return nlText;
  return desc;
}

// ---------------------------------------------------------------------------
// Confidence helpers
// ---------------------------------------------------------------------------

function confidenceLevel(score) {
  if (score == null) return { level: 'unknown', pct: 0 };
  const pct = Math.round(score * 100);
  if (pct >= 70) return { level: 'high', pct };
  if (pct >= 40) return { level: 'moderate', pct };
  return { level: 'low', pct };
}

function buildConfidenceSentence(score) {
  if (score == null) return null;
  const { level, pct } = confidenceLevel(score);

  if (level === 'high') {
    return `The system has high confidence (${pct}%) in this answer. The information retrieved closely matched your question and the system records strongly supported this response.`;
  }
  if (level === 'moderate') {
    return `The system has moderate confidence (${pct}%) in this answer. The retrieved information partially matched your question. Some details may benefit from verification.`;
  }
  return `The system has low confidence (${pct}%) in this answer. The system records had limited matches for your question, so this answer should be verified carefully.`;
}

// ---------------------------------------------------------------------------
// Main: collect, deduplicate, categorise, sort
// ---------------------------------------------------------------------------

function collectUnitsFromLedger(sourceItems) {
  const seenIds = new Set();
  return sourceItems
    .filter(item => {
      if (!item.node_id || seenIds.has(item.node_id)) return false;
      seenIds.add(item.node_id);
      return true;
    })
    .map(item => {
      const srcDoc = item.source_doc_name || '';
      const lbl = cleanLabel(item.label || '', item.text_sent || '', srcDoc);
      const desc = cleanDescription(item.text_sent || '');
      if (!lbl && !desc) return null;
      return {
        label: lbl,
        description: desc,
        sourceDoc: srcDoc,
        sourceSection: item.source_doc_section || '',
        createDate: formatDate(item.create_date),
        createdBy: item.created_by || '',
        nodeType: item.node_type || 'concept',
        sourceType: item.source_type || '',
        potency: 0,
      };
    })
    .filter(Boolean);
}

function collectUnitsLegacy(trace) {
  const seenIds = new Set();
  const all = [];

  const evidenceNodes = trace?.context_assembly?.evidence_nodes || [];
  const verbatimNodes = trace?.context_assembly?.verbatim_nodes || [];

  [...evidenceNodes, ...verbatimNodes].forEach(n => {
    if (!n.node_id || seenIds.has(n.node_id)) return;
    seenIds.add(n.node_id);
    const rawDesc = bestDescription(n);
    const srcDoc = n.source_doc_name || '';
    const lbl = cleanLabel(n.label || '', rawDesc, srcDoc);
    const desc = cleanDescription(rawDesc);
    if (!lbl && !desc) return;
    all.push({
      label: lbl,
      description: desc,
      sourceDoc: srcDoc,
      sourceSection: n.source_doc_section || '',
      createDate: formatDate(n.create_date),
      createdBy: n.created_by || '',
      nodeType: n.node_type || 'concept',
      potency: n.potency ?? 0,
    });
  });

  return all;
}

function collectUnits(trace) {
  const sourceItems = trace?.context_assembly?.prompt_source_items;
  if (Array.isArray(sourceItems) && sourceItems.length > 0) {
    return collectUnitsFromLedger(sourceItems);
  }
  return collectUnitsLegacy(trace);
}

function categorizeAndSort(units) {
  const buckets = { information: [], evidence: [], rules: [] };

  units.forEach(u => {
    const cat = categorize(u.nodeType);
    buckets[cat].push(u);
  });

  const byPotencyDesc = (a, b) => (b.potency ?? 0) - (a.potency ?? 0);
  buckets.information.sort(byPotencyDesc);
  buckets.evidence.sort(byPotencyDesc);
  buckets.rules.sort(byPotencyDesc);

  const strip = ({ potency, nodeType, sourceType, ...rest }) => rest;
  return {
    information: buckets.information.map(strip),
    evidence: buckets.evidence.map(strip),
    rules: buckets.rules.map(strip),
  };
}

// ---------------------------------------------------------------------------
// Activation trail — human-readable causal chain
// ---------------------------------------------------------------------------

function humanRelation(rel) {
  if (!rel) return 'relates to';
  const lower = rel.toLowerCase().trim();
  const GENERIC = new Set(['synapse', 'exc', 'inh', 'related to', 'related_to', '']);
  if (GENERIC.has(lower)) return 'relates to';
  return lower.replace(/_/g, ' ');
}

function buildTrailSteps(trace) {
  const chain = trace?.explanation_chain;
  if (!Array.isArray(chain) || chain.length === 0) return null;

  return chain.map(hop => ({
    label: cleanLabel(hop.source_label || '', hop.source_description || '', hop.source_doc_name || ''),
    description: cleanDescription(hop.source_description || ''),
    sourceDoc: hop.source_doc_name || '',
    relation: humanRelation(hop.relation),
  })).filter(s => s.label || s.description);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function generateExplainabilitySummary(metadata) {
  if (!metadata) return null;

  const trace = metadata.execution_trace || {};
  const score = metadata.score ?? null;
  const { level } = confidenceLevel(score);

  const units = collectUnits(trace);
  const { information, evidence, rules } = categorizeAndSort(units);
  const trailSteps = buildTrailSteps(trace);

  const totalUnits = information.length + evidence.length + rules.length;

  return {
    information,
    evidence,
    rules,
    trailSteps,
    totalUnits,
    confidenceSentence: buildConfidenceSentence(score),
    confidenceScore: score,
    confidenceLevel: level,
    insightId: metadata.insightId || null,
  };
}

export default generateExplainabilitySummary;
